import { BaseService } from "./BaseService";
import { IPartnerDashboardService } from "./interfaces/IPartnerDashboardService";
import {
  PartnerDateRangeQuery,
  PartnerHotelExportQuery,
  PartnerHotelFilterOption,
  PartnerHotelOverview,
  PartnerHotelOverviewQuery,
  PartnerHotelRevenueRank,
  PartnerRevenueLogsExportQuery,
  PartnerRevenueLogsQuery,
  PartnerRevenueLogsResponse,
  PartnerTotalRevenueSummary,
} from "@/types/partner-dashboard";
import {
  PartnerOrdersQuery,
  PartnerOrdersResponse,
  TenantPartnerOrdersSummary,
} from "@/types/partner-order";

class PartnerDashboardService
  extends BaseService
  implements IPartnerDashboardService
{
  private getBasePath = (tenantId: string) =>
    `/tenants/${tenantId}/partners/dashboard`;

  constructor() {
    super();
  }

  getHotels(
    tenantId: string,
    query: PartnerHotelOverviewQuery,
  ): Promise<PartnerHotelOverview[]> {
    return this.get<PartnerHotelOverview[]>(
      `${this.getBasePath(tenantId)}/hotels`,
      query,
    );
  }

  getHotelFilterOptions(tenantId: string): Promise<PartnerHotelFilterOption[]> {
    return this.get<PartnerHotelFilterOption[]>(
      `${this.getBasePath(tenantId)}/hotels/filter-options`,
    );
  }

  getTopRevenueHotel(
    tenantId: string,
    query: PartnerDateRangeQuery,
  ): Promise<PartnerHotelRevenueRank | null> {
    return this.get<PartnerHotelRevenueRank | null>(
      `${this.getBasePath(tenantId)}/top-revenue-hotel`,
      query,
    );
  }

  getLowestRevenueHotel(
    tenantId: string,
    query: PartnerDateRangeQuery,
  ): Promise<PartnerHotelRevenueRank | null> {
    return this.get<PartnerHotelRevenueRank | null>(
      `${this.getBasePath(tenantId)}/lowest-revenue-hotel`,
      query,
    );
  }

  getTotalRevenue(
    tenantId: string,
    query: PartnerDateRangeQuery,
  ): Promise<PartnerTotalRevenueSummary> {
    return this.get<PartnerTotalRevenueSummary>(
      `${this.getBasePath(tenantId)}/total-revenue`,
      query,
    );
  }

  async exportHotels(
    tenantId: string,
    query: PartnerHotelExportQuery,
  ): Promise<Blob> {
    const queryString = new URLSearchParams();
    queryString.append("from", query.from);
    queryString.append("to", query.to);
    queryString.append("format", query.format);
    query.hotelIds?.forEach((hotelId) =>
      queryString.append("hotelIds", hotelId),
    );

    const response = await fetch(
      `${this.baseUrl}${this.getBasePath(tenantId)}/hotels/export?${queryString.toString()}`,
      {
        method: "GET",
        headers: {
          ...(this.getAuthorizationHeader() ?? {}),
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to export hotels: ${response.status}`);
    }

    return response.blob();
  }

  getRevenueLogs(
    tenantId: string,
    query: PartnerRevenueLogsQuery,
  ): Promise<PartnerRevenueLogsResponse> {
    return this.get<PartnerRevenueLogsResponse>(
      `${this.getBasePath(tenantId)}/revenue-logs`,
      query,
    );
  }

  // Individual order rows across the tenant's hotels, and their summary
  // (contract confirmed 2026-09-27, backend dev@8379566). Neither is under
  // the `/partners/dashboard` base path — separate routes. Toast disabled:
  // the consuming UI shows its own friendly inline error (sim found a raw
  // error body rendered under the list, 2026-09-27) rather than a toast
  // built from the response.
  getOrders(
    tenantId: string,
    query: PartnerOrdersQuery,
  ): Promise<PartnerOrdersResponse> {
    return this.get<PartnerOrdersResponse>(
      `/tenants/${tenantId}/partners/orders`,
      query,
      { disableToast: true },
    );
  }

  getOrdersSummary(
    tenantId: string,
    query: PartnerDateRangeQuery,
  ): Promise<TenantPartnerOrdersSummary> {
    return this.get<TenantPartnerOrdersSummary>(
      `/tenants/${tenantId}/partners/orders/summary`,
      query,
      { disableToast: true },
    );
  }

  async exportRevenueLogs(
    tenantId: string,
    query: PartnerRevenueLogsExportQuery,
  ): Promise<Blob> {
    const queryString = new URLSearchParams();
    queryString.append("from", query.from);
    queryString.append("to", query.to);
    queryString.append("format", query.format);
    if (query.hotelId) {
      queryString.append("hotelId", query.hotelId);
    }

    const response = await fetch(
      `${this.baseUrl}${this.getBasePath(tenantId)}/revenue-logs/export?${queryString.toString()}`,
      {
        method: "GET",
        headers: {
          ...(this.getAuthorizationHeader() ?? {}),
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to export revenue logs: ${response.status}`);
    }

    return response.blob();
  }
}

export const partnerDashboardService = new PartnerDashboardService();
